import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

const AccountUnlockPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const [error, setError] = useState<string>('');
    const [isTokenExpired, setIsTokenExpired] = useState(false);
    const [requestingNewLink, setRequestingNewLink] = useState(false);

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    useEffect(() => {
        // Auto-unlock if we have both token and email
        if (token && email && !unlocked && !error) {
            handleUnlock();
        }
    }, [token, email]);

    const handleUnlock = async () => {
        if (!token || !email) {
            setError('Invalid unlock link. Missing token or email.');
            return;
        }

        setLoading(true);
        setError('');
        setIsTokenExpired(false);

        try {
            const result = await AuthService.unlockAccount(token, email);
            if (result.success) {
                setUnlocked(true);
                toast.success(result.message);
            } else {
                // Check if the error is about token expiry
                if (result.message && result.message.toLowerCase().includes('expired')) {
                    setIsTokenExpired(true);
                    setError('Your recovery link has expired. Please request a new one to unlock your account.');
                } else {
                    setError(result.message || 'Failed to unlock account');
                }
            }
        } catch (error: any) {
            // Check if the error is about token expiry
            if (error.message && error.message.toLowerCase().includes('expired')) {
                setIsTokenExpired(true);
                setError('Your recovery link has expired. Please request a new one to unlock your account.');
            } else {
                setError(error.message || 'Failed to unlock account');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRequestNewLink = async () => {
        if (!email) {
            toast.error('Email not available. Please try the recovery process again.');
            return;
        }

        setRequestingNewLink(true);

        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await fetch(`${API_URL}/users/request-new-unlock-link`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const result = await response.json();

            if (result.success) {
                toast.success(result.message);
                setError(''); // Clear the error since we've sent a new link
                setIsTokenExpired(false);
                navigate('/login');

            } else {
                toast.error(result.message || 'Failed to send new recovery link');
            }
        } catch (error) {
            console.error('Error requesting new link:', error);
            toast.error('Failed to send new recovery link');
        } finally {
            setRequestingNewLink(false);
        }
    };

    const handleLoginRedirect = () => {
        navigate('/login');
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
                        {unlocked ? (
                            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                            </svg>
                        ) : isTokenExpired ? (
                            <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : error ? (
                            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        ) : (
                            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        )}
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {unlocked ? 'Account Unlocked!' :
                            isTokenExpired ? 'Link Expired' :
                                error ? 'Unlock Failed' :
                                    'Unlock Your Account'}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {unlocked ?
                            'Your account has been successfully unlocked. You can now log in normally.' :
                            isTokenExpired ?
                                'Your recovery link has expired for security reasons. Request a new one below.' :
                                loading ? 'Unlocking your account...' :
                                    error ? 'There was a problem unlocking your account.' :
                                        'Processing your account unlock request...'
                        }
                    </p>
                </div>

                <div className="mt-8 space-y-6">
                    {error && (
                        <div className={`${isTokenExpired ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'} border px-4 py-3 rounded relative`} role="alert">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    {isTokenExpired ? (
                                        <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                                <div className="ml-3">
                                    <h3 className={`text-sm font-medium ${isTokenExpired ? 'text-orange-800' : 'text-red-800'}`}>
                                        {isTokenExpired ? 'Recovery Link Expired' : 'Unlock Failed'}
                                    </h3>
                                    <div className={`mt-2 text-sm ${isTokenExpired ? 'text-orange-700' : 'text-red-700'}`}>
                                        <p>{error}</p>
                                    </div>
                                    <div className="mt-4">
                                        <div className="-mx-2 -my-1.5 flex">
                                            <button
                                                onClick={handleRequestNewLink}
                                                disabled={requestingNewLink}
                                                className={`${isTokenExpired ? 'bg-orange-50 text-orange-800 hover:bg-orange-100 focus:ring-orange-600' : 'bg-red-50 text-red-800 hover:bg-red-100 focus:ring-red-600'} px-2 py-1.5 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                                            >
                                                {requestingNewLink ? 'Sending...' : 'Request New Recovery Link'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {unlocked && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative" role="alert">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-green-800">
                                        Success!
                                    </h3>
                                    <div className="mt-2 text-sm text-green-700">
                                        <p>Your account has been unlocked successfully. All failed login attempts have been reset.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-3 text-gray-600">Unlocking account...</span>
                        </div>
                    )}

                    <div className="flex flex-col space-y-3">
                        {unlocked ? (
                            <Button
                                onClick={handleLoginRedirect}
                                variant="primary"
                                className="w-full"
                            >
                                Continue to Login
                            </Button>
                        ) : !loading && !error && (
                            <Button
                                onClick={handleUnlock}
                                variant="primary"
                                loading={loading}
                                disabled={loading || !token || !email}
                                className="w-full"
                            >
                                {loading ? 'Unlocking...' : 'Unlock Account'}
                            </Button>
                        )}

                        {error && (
                            <Button
                                onClick={() => navigate('/login')}
                                variant="secondary"
                                className="w-full"
                            >
                                Back to Login
                            </Button>
                        )}
                    </div>

                    <div className="text-center">
                        <p className="text-sm text-gray-600">
                            Need help? {' '}
                            <button
                                onClick={() => navigate('/contact')}
                                className="font-medium text-blue-600 hover:text-blue-500"
                            >
                                Contact Support
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountUnlockPage; 